import {
    FancySelectInput,
    FancySelectOptions,
    StringGroupOption,
    StringOption,
    StringOptions,
} from '@/components/common/FancySelectInput';
import SelectCityState from '@/components/common/SelectCityState';
import SelectInput, {
    SelectInputOption,
} from '@/components/common/SelectInput';
import YesNoSelect from '@/components/common/YesNoSelect';
import {
    FormControl,
    FormControlProps,
    FormErrorMessage,
    FormHelperText,
    FormLabel,
    FormLabelProps,
    forwardRef,
    InputProps,
} from '@chakra-ui/react';
import { assert } from '@common/errors';
import { FormattedInputType } from '@common/inputs';
import { Country } from '@common/types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import SelectAddress from './AddressInput';
import CheckboxInput from './CheckboxInput';
import CountryInput from './CountryInput';
import DatePicker, { DatePickerOptions } from './DatePicker';
import { useValidation } from './Form';
import FormattedInput, {
    FormattedInputOptions,
    SliderConfig,
} from './FormattedInput';
import FormattedWithPlusOrMinusInput from './FormattedWithPlusOrMinusInput';
import RadioCardInputGroup from './RadioCardInputGroup';
import RadioInputGroup, {
    RadioInputGroupProps,
    RadioInputOption,
} from './RadioInputGroup';
import RecurrenceSelect from './RecurrenceSelect';
import SsnInput from './SsnInput';
import TransactionScheduleInput, {
    TransactionScheduleInputOptions,
} from './TransactionScheduleInput';
import UsaStateInput from './UsaStateInput';
import YearMonthInput, { YearMonthOptions } from './YearMonthInput';

type Validation = {
    validate: (value: any) => boolean;
    error: string;
};

export interface InputFieldProps
    extends Omit<FormControlProps, 'value' | 'onChange' | 'label'> {
    label?: string | JSX.Element;
    labelProps?: FormLabelProps;
    inputProps?: InputProps;
    radioProps?: RadioInputGroupProps['inputProps'];
    name: string;
    placeholder?: string;
    helpText?: string;
    type:
        | FormattedInputType
        | 'date'
        | 'recurrence'
        | 'radio'
        | 'radioCards'
        | 'select'
        | 'fancySelect'
        | 'country'
        | 'usaState'
        | 'cityState'
        | 'address'
        | 'checkbox'
        | 'transactionSchedulePlan'
        | 'ssn'
        | 'yearMonth'
        | 'yesNo';
    options?:
        | SelectInputOption[]
        | RadioInputOption[]
        | StringGroupOption[]
        | StringOption[];
    inputOptions?: FormattedInputOptions;
    fancySelectOptions?: FancySelectOptions<boolean, any>;
    transactionScheduleInputOptions?: TransactionScheduleInputOptions;
    value?: any;
    onChange: (value?: any) => void;
    tabIndex?: number;
    slider?: SliderConfig;
    showPlusOrMinusComponent?: boolean;
    dateOptions?: DatePickerOptions;
    yearMonthOptions?: YearMonthOptions;
    validations?: Validation[];
    validateOnBlur?: boolean;
    error?: string;
    iconElement?: JSX.Element;
    iconAddon?: JSX.Element;
    excludeCountries?: Country[];
}

const InputField = forwardRef<InputFieldProps, 'input' | 'select'>(
    (
        {
            label,
            name,
            placeholder,
            helpText,
            type,
            options,
            inputOptions,
            transactionScheduleInputOptions,
            fancySelectOptions,
            value: _value,
            onChange: _onChange,
            onBlur: _onBlur,
            tabIndex,
            labelProps = {},
            inputProps = {},
            radioProps = {},
            isRequired,
            slider,
            showPlusOrMinusComponent,
            dateOptions,
            yearMonthOptions,
            validations,
            validateOnBlur,
            autoFocus,
            error: _error,
            iconElement,
            iconAddon,
            excludeCountries,
            ...rest
        },
        ref
    ) => {
        const { value, onChange, onBlur, error } = useErrorHandling({
            value: _value,
            onChange: _onChange,
            onBlur: _onBlur,
            validations,
            validateOnBlur,
            isRequired,
        });
        let input;
        switch (type) {
            case 'recurrence': {
                input = (
                    <RecurrenceSelect
                        id={name}
                        name={name}
                        mt={'1px'}
                        placeholder={placeholder}
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        tabIndex={tabIndex}
                        variant={inputProps?.variant}
                        size={inputProps?.size}
                        isRequired={isRequired}
                        ref={ref}
                    />
                );
                break;
            }
            case 'yesNo': {
                input = (
                    <YesNoSelect
                        id={name}
                        name={name}
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        tabIndex={tabIndex}
                        isRequired={isRequired}
                        variant={inputProps?.variant}
                        ref={ref}
                    />
                );
                break;
            }
            case 'radio': {
                assert(options, 'radio components must include `options`');
                input = (
                    <RadioInputGroup
                        inputProps={radioProps}
                        id={name}
                        name={name}
                        value={value}
                        options={options as RadioInputOption[]}
                        onChange={onChange}
                        onBlur={onBlur}
                        tabIndex={tabIndex}
                        autoFocus={autoFocus}
                        ref={ref}
                    />
                );
                break;
            }
            case 'radioCards': {
                assert(options, 'radioCard components must include `options`');
                input = (
                    <RadioCardInputGroup
                        inputProps={radioProps}
                        id={name}
                        name={name}
                        value={value}
                        options={options as RadioInputOption[]}
                        onChange={onChange}
                        onBlur={onBlur}
                        tabIndex={tabIndex}
                        ref={ref}
                    />
                );
                break;
            }
            case 'select': {
                assert(options, 'select components must include `options`');
                input = (
                    <SelectInput
                        {...inputOptions}
                        id={name}
                        name={name}
                        placeholder={placeholder}
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        tabIndex={tabIndex}
                        options={options as SelectInputOption[]}
                        // TODO: Maybe just grab `pick` from lodash
                        variant={inputProps.variant}
                        size={inputProps.size}
                        fontSize={inputProps.fontSize}
                        fontWeight={inputProps.fontWeight}
                        isDisabled={inputProps.isDisabled}
                        isReadOnly={inputProps.isReadOnly}
                        autoFocus={autoFocus}
                        ref={ref}
                    />
                );
                break;
            }
            case 'fancySelect': {
                assert(
                    options || fancySelectOptions?.loadOptions,
                    'select components must include `options` or `loadOptions`'
                );
                input = (
                    <FancySelectInput
                        {...inputOptions}
                        {...fancySelectOptions}
                        id={name}
                        name={name}
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        tabIndex={tabIndex}
                        options={options as StringOptions}
                        isDisabled={inputProps.isDisabled}
                        isReadOnly={inputProps.isReadOnly}
                        autoFocus={autoFocus}
                        ref={ref}
                        size={inputProps.size}
                        variant={inputProps.variant}
                    />
                );
                break;
            }
            case 'country': {
                input = (
                    <CountryInput
                        id={name}
                        name={name}
                        placeholder={placeholder}
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        tabIndex={tabIndex}
                        // TODO: Maybe just grab `pick` from lodash
                        variant={inputProps.variant}
                        size={inputProps.size}
                        fontSize={inputProps.fontSize}
                        fontWeight={inputProps.fontWeight}
                        isDisabled={inputProps.isDisabled}
                        isReadOnly={inputProps.isReadOnly}
                        autoFocus={autoFocus}
                        ref={ref}
                        excludeCountries={excludeCountries}
                    />
                );
                break;
            }
            case 'usaState': {
                input = (
                    <UsaStateInput
                        id={name}
                        name={name}
                        placeholder={placeholder}
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        tabIndex={tabIndex}
                        // TODO: Maybe just grab `pick` from lodash
                        variant={inputProps.variant}
                        size={inputProps.size}
                        fontSize={inputProps.fontSize}
                        fontWeight={inputProps.fontWeight}
                        isDisabled={inputProps.isDisabled}
                        isReadOnly={inputProps.isReadOnly}
                        autoFocus={autoFocus}
                        ref={ref}
                    />
                );
                break;
            }
            case 'checkbox': {
                input = (
                    <CheckboxInput
                        {...inputOptions}
                        id={name}
                        name={name}
                        placeholder={placeholder}
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        tabIndex={tabIndex}
                        variant={inputProps.variant}
                        size={inputProps.size}
                        colorScheme={inputProps.colorScheme}
                        borderColor="blackAlpha.300"
                        my={2}
                        gap={2}
                        ref={ref}
                    >
                        {label}
                    </CheckboxInput>
                );
                break;
            }
            case 'date': {
                input = (
                    <DatePicker
                        id={name}
                        name={name}
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        options={dateOptions}
                        variant={inputProps.variant}
                        size={inputProps.size}
                    />
                );
                break;
            }
            case 'transactionSchedulePlan':
                input = (
                    <TransactionScheduleInput
                        transactionSchedule={value}
                        onChangeTransactionSchedule={onChange}
                        onBlur={onBlur}
                        showPlusOrMinusComponent={showPlusOrMinusComponent}
                        disabledText={
                            transactionScheduleInputOptions?.disabledText
                        }
                        fixedFreq={transactionScheduleInputOptions?.fixedFreq}
                        inputProps={inputProps}
                    />
                );
                break;
            case 'yearMonth':
                input = (
                    <YearMonthInput
                        yearMonth={value ?? ''}
                        onChangeYearMonth={onChange}
                        options={yearMonthOptions}
                        inputProps={inputProps}
                    />
                );
                break;
            case 'cityState':
                input = (
                    <SelectCityState
                        {...inputOptions}
                        id={name}
                        name={name}
                        placeholder={placeholder}
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        tabIndex={tabIndex}
                        options={options}
                        size={inputProps.size}
                        variant={inputProps.variant}
                        isDisabled={inputProps.isDisabled}
                        isReadOnly={inputProps.isReadOnly}
                        ref={ref}
                    />
                );
                break;
            case 'address':
                input = (
                    <SelectAddress
                        id={name}
                        name={name}
                        placeholder={placeholder}
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        tabIndex={tabIndex}
                        options={options}
                        size={inputProps.size}
                        variant={inputProps.variant}
                        isDisabled={inputProps.isDisabled}
                        isReadOnly={inputProps.isReadOnly}
                        autoFocus={autoFocus}
                    />
                );
                break;
            case 'ssn':
                input = (
                    <SsnInput
                        id={name}
                        name={name}
                        placeholder={placeholder}
                        tabIndex={tabIndex}
                        size={inputProps.size}
                        variant={inputProps.variant}
                    />
                );
                break;
            default:
                input =
                    showPlusOrMinusComponent &&
                    ['currency', 'float', 'integer', 'percent'].includes(
                        type
                    ) ? (
                        <FormattedWithPlusOrMinusInput
                            {...inputProps}
                            id={name}
                            name={name}
                            type={type}
                            options={inputOptions}
                            placeholder={placeholder}
                            value={value}
                            onChange={onChange}
                            onBlur={onBlur}
                            tabIndex={tabIndex}
                            forwardedRef={ref}
                            slider={slider}
                        />
                    ) : (
                        <FormattedInput
                            {...inputProps}
                            id={name}
                            name={name}
                            type={type}
                            options={inputOptions}
                            placeholder={placeholder}
                            value={value}
                            onChange={onChange}
                            onBlur={onBlur}
                            tabIndex={tabIndex}
                            forwardedRef={ref}
                            slider={slider}
                            iconElement={iconElement}
                            iconAddon={iconAddon}
                        />
                    );
        }

        return (
            <FormControl
                isInvalid={rest.isInvalid || !!error || !!_error}
                {...rest}
            >
                {type !== 'checkbox' && !!label && (
                    <FormLabel
                        htmlFor={name}
                        mb={{ base: 1, '2xl': 2 }}
                        {...labelProps}
                    >
                        {label}
                    </FormLabel>
                )}
                {input}
                {helpText && (
                    <FormHelperText fontSize="sm">{helpText}</FormHelperText>
                )}
                {(error || _error) && (
                    <FormErrorMessage>{error || _error}</FormErrorMessage>
                )}
            </FormControl>
        );
    }
);

export default InputField;

const useErrorHandling = ({
    value,
    onChange,
    onBlur,
    validations: _validations = [],
    validateOnBlur,
    isRequired,
}: Pick<
    InputFieldProps,
    | 'value'
    | 'onChange'
    | 'onBlur'
    | 'validations'
    | 'validateOnBlur'
    | 'isRequired'
>) => {
    const [error, setError] = useState('');
    const validations: InputFieldProps['validations'] = useMemo(
        () =>
            isRequired
                ? [
                      ..._validations,
                      {
                          validate: (v: any) => !!v,
                          error: 'Please complete the required field',
                      },
                  ]
                : _validations,
        [isRequired, _validations]
    );

    const handleChange = (value: any) => {
        // valueRef.current = value;
        setError('');
        onChange?.(value);
    };

    const handleBlur = (event: any) => {
        if (validateOnBlur) handleValidation();
        onBlur?.(event);
    };

    const handleValidation = useCallback(() => {
        if (!validations?.length) return true;
        const results = validations.map(({ validate }) => validate(value));
        const isValidated = results.every((result) => !!result);
        if (isValidated) {
            setError('');
            return true;
        } else {
            const error = validations[results.indexOf(false)].error;
            setError(error);
            return false;
        }
    }, [validations, value]);

    // If the validatons change, then reset error state
    useEffect(() => setError(''), [validations]);

    // subscribes the validation handler to the form context
    useValidation(handleValidation);

    return {
        value,
        onChange: handleChange,
        onBlur: handleBlur,
        error,
    };
};
